// src/pages/ContactUs.js
import React, { useState } from 'react';
import './ContactUs.css';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    contactGoal: 'Other',
    propertyType: '',
    location: '',
    serviceType: '',
    scope: '',
    timeline: '',
    message: ''
  });

  const [showServiceFields, setShowServiceFields] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'contactGoal') {
      setShowServiceFields(value === 'Service Enquiries');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        contactGoal: formData.contactGoal,
        ...(showServiceFields && {
          propertyType: formData.propertyType,
          location: formData.location,
          serviceType: formData.serviceType,
          scope: formData.scope,
          timeline: formData.timeline,
        }),
        message: formData.message,
      };

      const response = await fetch('https://5rzkh3t6ve5dx3b2jnna3digfe0pxnpe.lambda-url.ap-southeast-2.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        alert('Thank you for your enquiry. We will get back to you soon.');
        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          contactGoal: 'Other',
          propertyType: '',
          location: '',
          serviceType: '',
          scope: '',
          timeline: '',
          message: ''
        });
        setShowServiceFields(false);
      } else {
        throw new Error(result.message || 'Failed to send message.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was a problem sending your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const propertyTypes = ['commercial', 'residential', 'education', 'mixed-use'];

  return (
    <div className="contact-us">
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-text">
            <h1 className="page-title">CONTACT US</h1>
            <p className="page-subtitle">
              Get in touch with Vanguard Facilities Services to discuss how we can support, protect, and enhance
              your assets.
            </p>
            <p className="page-subtitle">
              Whether you are inquiring about our services, requesting a site visit, or exploring a potential
              partnership, our team is ready to assist.
            </p>
          </div>

          <div className="contact-hero-form">
            <div className="form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name and Surname *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Enter your company name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email or Phone Number *</label>
                  <input
                    type="text"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email or phone number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactGoal">Contact Goal</label>
                  <select
                    id="contactGoal"
                    name="contactGoal"
                    value={formData.contactGoal}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="Service Enquiries">Service Enquiries</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {showServiceFields && (
                  <div className="service-fields">
                    <h4 className="service-fields-title">Service Enquiry Details</h4>
                    
                    <div className="form-group">
                      <label htmlFor="propertyType">Property Type</label>
                      <select
                        id="propertyType"
                        name="propertyType"
                        value={formData.propertyType}
                        onChange={handleChange}
                        className="form-select"
                      >
                        <option value="">Select property type</option>
                        {propertyTypes.map(type => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="location">Location of the Asset</label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter property location"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="serviceType">Type of Service Required</label>
                      <input
                        type="text"
                        id="serviceType"
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        placeholder="Describe the service needed"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="scope">Scope and Urgency</label>
                      <textarea
                        id="scope"
                        name="scope"
                        value={formData.scope}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Describe the scope of work and urgency"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="timeline">Preferred Timeline</label>
                      <input
                        type="text"
                        id="timeline"
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleChange}
                        placeholder="e.g., ASAP, within 2 weeks, specific date"
                      />
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    placeholder="Enter your message"
                  />
                </div>

                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="service-enquiries-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-with-line">Service Enquiries</h2>
            <div className="title-line"></div>
          </div>
          
          <div className="service-enquiries-content">
            <div className="service-enquiries-left">
              <p className="service-enquiries-text">
                If you would like to engage Vanguard or<br />
                request a site assessment, please include the<br />
                following information in your enquiry:
              </p>
            </div>
            <div className="service-enquiries-right">
              <ul className="service-enquiries-list">
                <li>Property type<br /><span className="list-subtext">(commercial, residential, education, mixed-use)</span></li>
                <li>Location of the asset</li>
                <li>Type of service required</li>
                <li>Scope and urgency</li>
                <li>Preferred timeline</li>
                <li className="list-note">Our team will review your enquiry and respond<br />promptly to arrange the appropriate next steps.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="general-enquiries-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title-with-line">General Enquiries</h2>
            <div className="title-line"></div>
          </div>
          
          <div className="general-enquiries-text">
            <p>
              For all general enquiries, service requests, or information about our capabilities, please contact us<br />
              using the details below or submit an enquiry through our contact form.
            </p>
          </div>
          
          <div className="general-enquiries-content">
            <div className="contact-info-left">
              <div className="info-item">
                <strong>Email</strong>
                <p>contact@vanguard.fsg.com.au</p>
              </div>
              <div className="info-item">
                <strong>Phone</strong>
                <p>+61 405 352 347</p>
              </div>
            </div>
            <div className="contact-info-right">
              <div className="info-item">
                <strong>Location</strong>
                <p>Sydney, NSW, Australia</p>
              </div>
              <div className="info-item">
                <strong>Business Hours</strong>
                <p>Monday – Friday<br />8:00am – 5:00pm</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;